import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Lock, Zap, ArrowRight, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';

// Inline gate: replaces a button with a lock message
export const PremiumGate = ({ children, feature, fallback }) => {
  const { user } = useAuth();
  if (user?.is_premium) return children;
  return fallback || <LockedButton feature={feature} />;
};

// Lock button that shows activation prompt
export const LockedButton = ({ feature, className = '', size = 'sm' }) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        size={size}
        className={`text-muted-foreground border-dashed ${className}`}
        onClick={() => setShowModal(true)}
        data-testid={`locked-${feature?.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <Lock className="w-3.5 h-3.5 mr-1.5" />
        {feature || 'Pro Feature'}
      </Button>
      {showModal && <ActivateModal feature={feature} onClose={() => setShowModal(false)} />}
    </>
  );
};

// Full-width locked overlay for sections
export const LockedOverlay = ({ feature, message }) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <div
        className="flex flex-col items-center justify-center py-8 px-4 text-center cursor-pointer hover:bg-muted/30 rounded-xl transition-colors"
        onClick={() => setShowModal(true)}
        data-testid={`locked-overlay-${feature?.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
          <Lock className="w-5 h-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">{message || `Subscribe to unlock ${feature}`}</p>
        <p className="text-xs text-muted-foreground mt-1">Upgrade to Pro for full access</p>
        <Button size="sm" className="mt-3 gap-1.5 text-xs" variant="default">
          <Zap className="w-3.5 h-3.5" /> Activate Account
        </Button>
      </div>
      {showModal && <ActivateModal feature={feature} onClose={() => setShowModal(false)} />}
    </>
  );
};

// Activation modal
export const ActivateModal = ({ feature, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleActivate = async () => {
    setLoading(true);
    try {
      const res = await api.createCheckout('premium');
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        toast.error('Unable to start checkout. Please try again.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 fade-in duration-200" onClick={e => e.stopPropagation()} data-testid="activate-modal">
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5f8a] p-6 text-center relative">
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Zap className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white font-['Outfit']">Activate Pro Account</h2>
          <p className="text-white/70 text-sm mt-1">$20/month · Cancel anytime</p>
        </div>
        <div className="p-6 space-y-4">
          {feature && (
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">{feature}</span> is a Pro feature
              </p>
            </div>
          )}
          <ul className="space-y-2.5 text-sm">
            <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />Download, copy & install signatures</li>
            <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />Unlimited AI Networking Coach</li>
            <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />Create groups & host events</li>
            <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />Marketplace ad slot placement</li>
            <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />Earn 50% from ad slots</li>
          </ul>
          <Button className="w-full h-11 text-sm font-semibold group" onClick={handleActivate} disabled={loading} data-testid="activate-checkout-btn">
            {loading ? 'Starting checkout...' : (
              <>Activate Pro · $20/mo <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
            )}
          </Button>
          <button onClick={onClose} className="w-full text-xs text-muted-foreground hover:text-foreground text-center">Maybe later</button>
        </div>
      </div>
    </div>
  );
};
